import React, { ReactNode, useLayoutEffect, useRef } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { PaymentSpeedIcon, TickCircleIcon, WarningIcon } from '@motorway/mw-highway-code';

import { GENERIC_ANALYTICS_EVENTS } from 'Utilities/analytics/analyticsGenericEvents';
import { TTC_CAR_CHECK_RESULTS_ROUTE } from 'Utilities/routing';

import { Content } from 'LegacySBCRA/Content/Content';

import { CAR_CHECK_SNOWPLOW } from './CarCheckerHero/SubComponents/CarCheckerForm/CarCheckerForm.helpers';
import type { CarCheckReportProps, Detail, DetailTableProps, ReportSectionProps, StatusCardProps } from './CarCheckReport.types';
import { CarCheckReportError } from './CarCheckReportError';

import styles from './CarCheckReport.scss';

const isAvailable = (value?: string | null) => Boolean(value && value !== 'Not Available');

const pr = new Intl.PluralRules('en-GB', { type: 'ordinal' });
const suffixes: Partial<Record<Intl.LDMLPluralRule, string>> = {
  few: 'rd',
  one: 'st',
  two: 'nd',
};

const getOrdinalSuffix = (day: number): string => suffixes[pr.select(day)] ?? 'th';

const formatCollectionDate = (date: Date): ReactNode => {
  const day = date.getDate();
  return (<>{day}<small>{getOrdinalSuffix(day)}</small></>);
};

const formatDate = (date?: string) => {
  if (!date || date.startsWith('0001-')) {
    return undefined;
  }

  return (
    <>
      {formatCollectionDate(new Date(date))}{' '}
      {new Intl.DateTimeFormat('en-GB', {
        month: 'short',
        year: 'numeric',
      }).format(new Date(date))}
    </>
  );
};

const formatChargeTime = (minutes?: string) => {
  if (!minutes) {
    return undefined;
  }

  const totalMinutes = Number(minutes);

  if (!Number.isFinite(totalMinutes)) {
    return undefined;
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  return [hours ? `${hours} hr` : '', remainingMinutes ? `${remainingMinutes} mins` : ''].filter(Boolean).join(' ');
};

const getEvType = (type?: string) => {
  if (type === 'BEV') {
    return 'Full Battery Electric - BEV';
  }

  if (type === 'PHEV') {
    return 'Plug-in Hybrid - PHEV';
  }

  return type;
};

const getDriveType = (propulsion?: string) => ({
  awd: 'All Wheel Drive',
  front: 'Front Wheel Drive',
  rear: 'Rear Wheel Drive',
})[propulsion?.toLowerCase() ?? ''] ?? propulsion;

const DetailTable = ({ details }: DetailTableProps) => {
  const visibleDetails = details.filter(({ value }) => value !== undefined && value !== null && value !== '');

  if (!visibleDetails.length) {
    return null;
  }

  return (
    <dl className={styles.detailTable}>
      {visibleDetails.map(({ label, value }) => (
        <div key={label} className={styles.detailRow}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
};

const ReportSection = ({ children, className, title }: ReportSectionProps) => (
  <section className={cx(styles.section, className)}>
    <h2>{title}</h2>
    {children}
  </section>
);

const StatusCard = ({ detail, isPositive, label, value }: StatusCardProps) => {
  const Icon = isPositive ? TickCircleIcon : WarningIcon;

  return (
    <section className={cx(styles.statusCard, { [styles.alert]: !isPositive })}>
      <Icon data-thc-icon size='large' />
      <div>
        <span className={styles.statusLabel}>{label}</span>
        <strong>{value}{detail && <small>{detail}</small>}</strong>
      </div>
    </section>
  );
};

const LinkCard = ({ vrm }: { vrm: string }) => {
  const Icon = PaymentSpeedIcon;

  return (
    <section className={cx(styles.statusCard, styles.linkCard)}>
      <Link onClick={() => {
        GENERIC_ANALYTICS_EVENTS.CTA_CLICK({
          ...CAR_CHECK_SNOWPLOW,
          button_label: 'Vehicle value',
          label: 'Find out what it\'s worth',
          name: 'CarCheckerHero_Valuation',
          url: `/${vrm}`,
        });
      }} to={`/${vrm}`}>
        <Icon data-thc-icon size='medium' />
        <div>
          <span className={styles.statusLabel}>Vehicle value</span>
          <strong>Find out what it's worth</strong>
        </div>
      </Link>
    </section>
  );
};

export const CarCheckReport = ({ response }: CarCheckReportProps) => {
  const history = useHistory();
  const mainRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    history.push(TTC_CAR_CHECK_RESULTS_ROUTE);

    requestAnimationFrame(() => {
      mainRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  if (!response.data || response.data.HasError || !('BasicVehicleDetailsModel' in response.data)) {
    if (response.data?.ErrorMessage === 'Vehicle not found') {
      return (<CarCheckReportError message="Vehicle not found" />);
    }
    return (<CarCheckReportError message="Something went wrong" />);
  }

  const { BasicVehicleDetailsModel: vehicle, Vrm } = response.data;
  const electricVehicle = vehicle.ElectricVehicleData;
  const chargingCosts = electricVehicle?.ElectricVehicleChargingCosts;
  const motResults = vehicle.MotResultsSummary?.MotResults ?? [];
  const recalls = vehicle.VehicleRecalls ?? [];
  const motValid = vehicle.MotStatusDescription?.toLowerCase() === 'valid';
  const taxValid = vehicle.RoadTaxStatusDescription?.toLowerCase() === 'taxed';

  const vehicleDetails: Detail[] = [
    { label: 'Manufacturer', value: vehicle.Make },
    { label: 'Model', value: vehicle.Model },
    { label: 'Model detail', value: isAvailable(vehicle.ExtendedSpecificationDetails) ? vehicle.ExtendedSpecificationDetails : undefined },
    { label: 'Colour', value: vehicle.Colour },
    { label: 'Vehicle type', value: vehicle.VehicleType },
    { label: 'Body style', value: vehicle.BodyStyle },
    { label: 'Fuel type', value: vehicle.FuelType },
    { label: 'Engine size', value: vehicle.CylinderCapacity !== '0 cc' ? vehicle.CylinderCapacity : undefined },
    { label: 'Euro status', value: isAvailable(vehicle.EuroStatus) ? vehicle.EuroStatus : undefined },
    { label: 'BHP', value: vehicle.Bhp },
    { label: 'Vehicle age', value: vehicle.Age },
    { label: 'Registered date', value: vehicle.DateOfFirstRegistration },
    { label: 'V5C issue date', value: vehicle.DateOfLastV5CIssued },
    { label: 'Year of manufacture', value: vehicle.YearOfManufacture },
  ];

  const HAS_IMAGE = vehicle.VehicleImageUrl && vehicle.VehicleImageUrl.match(/^http/);
  const HAS_VALUE = (str: string | undefined) => !(str ?? '').match(/Not available/i);

  return (
    <main ref={mainRef} className={styles.component}>
      <Content>
        <section className={cx(styles.vehicleOverview, { [styles.noImage]: !HAS_IMAGE })}>
          <div>
            <span className={styles.eyebrow}>Basic report for</span>
            <h1>{vehicle.Make} {HAS_VALUE(vehicle.Model) ? vehicle.Model : ''}</h1>
            {HAS_VALUE(vehicle.ExtendedSpecificationDetails) && <small>{vehicle.ExtendedSpecificationDetails}</small>}
            <div className={styles.statusGrid}>
              <LinkCard vrm={Vrm} />
              <StatusCard
                isPositive={!vehicle.Exported}
                label='Export status'
                value={vehicle.Exported ? 'Recorded as exported' : 'Not exported'}
              />
              <StatusCard
                detail={[vehicle.DateMotDue, vehicle.DaysLeftUntilMotDue?.trim()].filter(Boolean).join(' · ')}
                isPositive={motValid}
                label='MOT status'
                value={vehicle.MotStatusDescription || 'Not available'}
              />
              <StatusCard
                detail={[vehicle.DateRoadTaxDue, vehicle.DaysLeftUntilRoadTaxDue?.trim()].filter(Boolean).join(' · ')}
                isPositive={taxValid}
                label='Road tax status'
                value={vehicle.RoadTaxStatusDescription || 'Not available'}
              />
            </div>
          </div>
          {HAS_IMAGE && (
            <div className={styles.imageWrapper}>
              <img alt={`${vehicle.Make} ${vehicle.Model || ''}`} src={vehicle.VehicleImageUrl} />
            </div>
          )}
        </section>

        <div className={styles.columns}>
          <div>
            <ReportSection title='Vehicle details'>
              <DetailTable details={vehicleDetails} />
            </ReportSection>

            {vehicle.InsuranceGroup && (
              <ReportSection title='Insurance'>
                <DetailTable details={[{ label: 'Insurance group', value: vehicle.InsuranceGroup }]} />
              </ReportSection>
            )}

            {vehicle.ImperialCombined_Economy && (
              <ReportSection title='Fuel economy'>
                <DetailTable details={[
                  { label: 'Urban', value: `${vehicle.ImperialUrban_Economy} MPG` },
                  { label: 'Extra-urban', value: `${vehicle.ImperialExtraUrban_Economy} MPG` },
                  { label: 'Combined', value: `${vehicle.ImperialCombined_Economy} MPG` },
                ]} />
              </ReportSection>
            )}

            {isAvailable(vehicle.TopSpeed) && (
              <ReportSection title='Vehicle performance'>
                <DetailTable details={[
                  { label: 'Top speed', value: `${vehicle.TopSpeed} MPH` },
                  { label: '0-60 MPH', value: isAvailable(vehicle.VehicleAccelerationMph) ? `${vehicle.VehicleAccelerationMph} secs` : undefined },
                ]} />
              </ReportSection>
            )}

            {chargingCosts?.DomesticChargingCost100Miles && (
              <ReportSection title='Real-world driving costs'>
                <p className={styles.sectionIntro}>What is the electricity cost for travelling these distances?</p>
                <strong className={styles.subsectionHeading}>Home charging</strong>
                <DetailTable details={[
                  { label: '1 mile', value: chargingCosts.DomesticChargingCost1Mile },
                  { label: '100 miles', value: chargingCosts.DomesticChargingCost100Miles },
                  { label: '12,000 miles', value: chargingCosts.DomesticChargingCost12kMiles },
                ]} />
                <strong className={styles.subsectionHeading}>Public charging</strong>
                <DetailTable details={[
                  { label: '1 mile', value: chargingCosts.PublicChargingCost1Mile },
                  { label: '100 miles', value: chargingCosts.PublicChargingCost100Miles },
                  { label: '12,000 miles', value: chargingCosts.PublicChargingCost12kMiles },
                ]} />
                <p className={styles.note}>
                  Electricity costs: {chargingCosts.DomesticChargingPenceCostPerKwh}p (home) and{' '}
                  {chargingCosts.PublicChargingPenceCostPerKwh}p (public) per kWh.
                </p>
              </ReportSection>
            )}

            <ReportSection className={styles.recallSection} title='Vehicle safety recall notices'>
              {recalls.length ? (
                <div className={styles.recallList}>
                  {recalls.map((recall) => (
                    <article key={recall.RecallsNumber}>
                      <span>{recall.RecallsNumber} · {formatDate(recall.LaunchDate)}</span>
                      <h3>{recall.Concern}</h3>
                      <p><strong>Remedy:</strong> {recall.Remedy}</p>
                    </article>
                  ))}
                </div>
              ) : <p>No safety recall notices found for this vehicle.</p>}
            </ReportSection>
          </div>

          <div>
            <ReportSection title='Road tax information'>
              <DetailTable details={[
                { label: '12 months cost', value: vehicle.TwelveMonthsTaxRate ? `£${vehicle.TwelveMonthsTaxRate}` : undefined },
                { label: '6 months cost', value: vehicle.SixMonthsTaxRate ? `£${vehicle.SixMonthsTaxRate}` : undefined },
              ]} />
              <p className={styles.note}>Road tax rates are indicative. Check with DVLA to confirm current rates.</p>
            </ReportSection>

            {vehicle.UlezCompliant !== undefined && vehicle.UlezCompliant !== null && (
              <ReportSection title='Clean air zones'>
                <StatusCard
                  isPositive={vehicle.UlezCompliant}
                  label='ULEZ status'
                  value={vehicle.UlezCompliant ? 'ULEZ compliant' : 'ULEZ charge applies'}
                />
              </ReportSection>
            )}

            {vehicle.Co2Marker && (
              <ReportSection title='Emissions information'>
                <DetailTable details={[
                  { label: 'CO₂ output', value: `${vehicle.Co2Emissions} g/km (${vehicle.Co2Marker.toUpperCase()})` },
                ]} />
                <div className={styles.emissionsBand} data-band={vehicle.Co2Marker.toUpperCase()}>
                  Emissions band {vehicle.Co2Marker.toUpperCase()}
                </div>
              </ReportSection>
            )}

            {electricVehicle && (
              <ReportSection title='Electric vehicle data'>
                <DetailTable details={[
                  {
                    label: 'Model',
                    value: [
                      electricVehicle.Vehicle_Model,
                      electricVehicle.Drivetrain_Type === 'BEV' ? electricVehicle.Vehicle_Model_Version : undefined,
                    ].filter(Boolean).join(' '),
                  },
                  { label: 'EV type', value: getEvType(electricVehicle.Drivetrain_Type) },
                  { label: 'Drive type', value: getDriveType(electricVehicle.Drivetrain_Propulsion) },
                  {
                    label: 'Starting price',
                    value: electricVehicle.Price_From_UK
                      ? new Intl.NumberFormat('en-GB', { currency: 'GBP', maximumFractionDigits: 0, style: 'currency' })
                        .format(Number(electricVehicle.Price_From_UK))
                      : undefined,
                  },
                  { label: 'Real range', value: electricVehicle.Range_Real ? `${electricVehicle.Range_Real} miles` : undefined },
                  { label: 'Maximum range', value: electricVehicle.Range_WLTP ? `${electricVehicle.Range_WLTP} miles` : undefined },
                  {
                    label: 'Usable battery',
                    value: electricVehicle.Battery_Capacity_Useable
                      ? `${electricVehicle.Battery_Capacity_Useable} kWh`
                      : undefined,
                  },
                  {
                    label: 'Battery warranty',
                    value: electricVehicle.Battery_Warranty_Period
                      ? `${electricVehicle.Battery_Warranty_Period} years${electricVehicle.Battery_Warranty_Mileage ? ` / ${electricVehicle.Battery_Warranty_Mileage} miles` : ''}`
                      : undefined,
                  },
                  {
                    label: 'Plug type',
                    value: [electricVehicle.Charge_Plug, electricVehicle.Fastcharge_Plug].filter(Boolean).join(' / '),
                  },
                  { label: 'Plug location', value: electricVehicle.Charge_Plug_Location },
                  { label: 'Slow charge (0% to 100%)', value: formatChargeTime(electricVehicle.Charge_Standard_ChargeTime) },
                  { label: 'Rapid charge (10% to 80%)', value: formatChargeTime(electricVehicle.Fastcharge_ChargeTime) },
                ]} />
              </ReportSection>
            )}

            {chargingCosts && (
              <ReportSection title='Real-world charging costs'>
                <p className={styles.sectionIntro}>What does it cost to charge this vehicle?</p>
                <strong className={styles.subsectionHeading}>Home charging</strong>
                <DetailTable details={[
                  { label: '0% to 100%', value: chargingCosts.DomesticChargingCost0To100Percent },
                  { label: '20% to 80%', value: chargingCosts.DomesticChargingCost20To80Percent },
                ]} />
                <strong className={styles.subsectionHeading}>Public charging</strong>
                <DetailTable details={[
                  { label: '0% to 100%', value: chargingCosts.PublicChargingCost0To100Percent },
                  { label: '20% to 80%', value: chargingCosts.PublicChargingCost20To80Percent },
                ]} />
                <p className={styles.note}>
                  Electricity costs: {chargingCosts.DomesticChargingPenceCostPerKwh}p (home) and{' '}
                  {chargingCosts.PublicChargingPenceCostPerKwh}p (public) per kWh.
                </p>
              </ReportSection>
            )}

            {vehicle.FuelCost1Mile && (
              <ReportSection title='Real-world driving costs'>
                <DetailTable details={[
                  { label: 'Cost per mile', value: vehicle.FuelCost1Mile },
                  { label: 'Cost per 100 miles', value: vehicle.FuelCost100Miles },
                  { label: 'Cost per 12,000 miles', value: vehicle.FuelCost12kMiles },
                ]} />
              </ReportSection>
            )}

            {motResults.length > 0 && (
              <ReportSection title='MOT history & mileage'>
                {vehicle.MotResultsSummary?.MileageIssueIdentified && (
                  <span className={cx(styles.warning, styles.alert)}>
                    <WarningIcon />A possible mileage discrepancy was identified.
                  </span>
                )}
                <div className={styles.historyList}>
                  {motResults.map((result) => {
                    const notices = [...(result.RefusalReasons_V2 ?? []), ...(result.AdvisoryNotices_V2 ?? [])];

                    return (
                      <details
                        key={`${result.DateOfTest}-${result.ResultText}`}
                        onToggle={(event) => {
                          if (!event.currentTarget.open) {
                            return;
                          }

                          GENERIC_ANALYTICS_EVENTS.POPUP_LOAD_EVENT({
                            category: CAR_CHECK_SNOWPLOW.category,
                            label: 'CarCheckReport_ButtonClick',
                            name: 'CarCheckReport_MOT_Opened',
                          });
                        }}
                      >
                        <summary>
                          <span className={cx({ [styles.alert]: result.ResultText !== 'Pass' })}>
                            {result.ResultText === 'Pass' ? <TickCircleIcon /> : <WarningIcon />}
                            {result.ResultText}
                          </span>
                          <span>· {formatDate(result.DateOfTest)}</span>
                          <span>
                            {result.OdometerModel?.OdometerReading.toLocaleString()}
                            &nbsp;
                            {result.OdometerModel?.OdometerFormat}
                          </span>
                        </summary>
                        <div className={styles.historyDetails}>
                          {
                            formatDate(result.TestExpiryDate) && (
                              <p>Expiry date: <strong>{formatDate(result.TestExpiryDate)}</strong></p>
                            )
                          }
                          {notices.length ? (
                            <ul>
                              {notices.map((notice) => <li key={`${notice.Type}-${notice.Text}`}><strong>{notice.Type}:</strong> {notice.Text}</li>)}
                            </ul>
                          ) : <p>No advisory or failure notices recorded.</p>}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </ReportSection>
            )}
          </div>
        </div>

      </Content>
    </main>
  );
};
